import { createSignal } from 'solid-js';
import { PolylineMeasurement } from '@technology/web3d-plugin-markup';
import type {
  MeasurementsCapabilityProvider,
  PolylineMeasurementType,
} from '@trimble/web3d-providers';
import { createBaseMeasurementController } from '@trimble/measurements-shared';
import type { PolylineMeasurementController } from './polyline-measurement.types';

export function createPolylineMeasurementController(
  provider: MeasurementsCapabilityProvider
): PolylineMeasurementController {
  const [showSegmentSlopes, setShowSegmentSlopesSignal] = createSignal(false);
  const [showSegmentDistances, setShowSegmentDistancesSignal] =
    createSignal(false);
  const [showVerticalMeasurements, setShowVerticalMeasurementsSignal] =
    createSignal(false);
  const [showHorizontalMeasurements, setShowHorizontalMeasurementsSignal] =
    createSignal(false);

  const base = createBaseMeasurementController<PolylineMeasurementType>(
    provider,
    {
      applyMarkupProperties: (markup) => {
        (markup as PolylineMeasurement).showSegmentSlopes = showSegmentSlopes();
        (markup as PolylineMeasurement).showSegmentDistances =
          showSegmentDistances();
        (markup as PolylineMeasurement).showVerticalMeasurements =
          showVerticalMeasurements();
        (markup as PolylineMeasurement).showHorizontalMeasurements =
          showHorizontalMeasurements();
      },
      getExtraUpdateFields: () => ({
        showSegmentDistances: showSegmentDistances(),
        showSegmentSlopes: showSegmentSlopes(),
        showVerticalMeasurements: showVerticalMeasurements(),
        showHorizontalMeasurements: showHorizontalMeasurements(),
      }),
      onInitialize: (measurement) => {
        setShowSegmentSlopesSignal(measurement.showSegmentSlopes ?? false);
        setShowSegmentDistancesSignal(
          measurement.showSegmentDistances ?? false
        );
        setShowVerticalMeasurementsSignal(
          measurement.showVerticalMeasurements ?? false
        );
        setShowHorizontalMeasurementsSignal(
          measurement.showHorizontalMeasurements ?? false
        );
      },
    }
  );

  const setShowSegmentSlopes = (value: boolean): void => {
    setShowSegmentSlopesSignal(value);
    base.update();
  };

  const setShowSegmentDistances = (value: boolean): void => {
    setShowSegmentDistancesSignal(value);
    base.update();
  };

  const setShowVerticalMeasurements = (value: boolean): void => {
    setShowVerticalMeasurementsSignal(value);
    base.update();
  };

  const setShowHorizontalMeasurements = (value: boolean): void => {
    setShowHorizontalMeasurementsSignal(value);
    base.update();
  };

  return {
    type: 'polyline',
    ...base,
    showSegmentSlopes,
    showSegmentDistances,
    showVerticalMeasurements,
    showHorizontalMeasurements,
    setShowSegmentSlopes,
    setShowSegmentDistances,
    setShowVerticalMeasurements,
    setShowHorizontalMeasurements,
  };
}
